import { useEffect } from 'react';
import { useAuth } from '@/lib/AuthContext';
import { useAuthStore } from '@/stores/authStore';

/**
 * Hydrates the new Zustand authStore once the legacy AuthProvider has
 * confirmed the session. Calling refresh() before the legacy /auth/me
 * succeeds would race and 401. Once Phase 6 retires the legacy
 * AuthContext, this bridge can move into App.tsx directly.
 *
 * Renders nothing.
 */
export function AuthBootstrap() {
  // 2026-09-15 — `isLoadingAuth` est lu EN PLUS de `isAuthenticated`. Le
  // legacy AuthContext demarre a isAuthenticated=false / isLoadingAuth=true
  // et ne tranche qu'apres son propre /auth/me. Sans ce garde, le premier
  // rendu tombait dans la branche `else` et appelait reset(), qui repose
  // isLoading=false sur l'authStore — donc RequireRole concluait « pas de
  // session » sur une session parfaitement valide et redirigeait vers
  // /Login. Cf. BUGS-OUVERTS.md (AUTH-1).
  const { isAuthenticated, isLoadingAuth, authError } = useAuth() as {
    isAuthenticated: boolean;
    isLoadingAuth: boolean;
    authError?: { type?: string } | null;
  };
  const refresh = useAuthStore((s) => s.refresh);
  const reset = useAuthStore((s) => s.reset);
  const markUnavailable = useAuthStore((s) => s.markUnavailable);
  const unavailable = authError?.type === 'unavailable';

  useEffect(() => {
    // Tant que le legacy n'a pas tranche, on ne touche a rien : l'authStore
    // reste sur son isLoading=true initial et le garde affiche son spinner.
    if (isLoadingAuth) return;
    if (isAuthenticated) {
      void refresh();
    } else if (unavailable) {
      // A 429 or a 5xx is not a logout (RUI-13).
      markUnavailable();
    } else {
      reset();
    }
  }, [isAuthenticated, isLoadingAuth, unavailable, refresh, reset, markUnavailable]);

  return null;
}
